
import CadastroPlanilhaTrechos from "./CadastroPlanilhaTrechos.vue";
import EntitySelectorOrgaoAet from "../common/EntitySelectorOrgaoAet.vue";
import { PaginationResult, defaultResults } from "../../pagination";
import { useAuth, PermissionPayload } from "../../modules/auth";
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  PropType,
  Ref,
  watch
} from "vue";
import { RouteLocationNormalizedLoaded, useRouter } from "vue-router";
import {
  PlanilhaTrechosPayload,
  PlanilhaTrechosFilterPayload,
  usePlanilhaTrechosService
} from "../../modules/business/trecho/planilhaTrechosBusiness";
import { clearObject, copyObject } from "../../modules/utils";
import {
  ClientePayload,
  useClienteService,
  useClienteLogadoService
} from "../../modules/business/cliente/clienteBusiness";
import {
  indexesOfElement,
  addSetElement,
  toggleElement,
  format
} from "../../modules/utils";
import { focusOnLastElement } from "../../modules/globalAppProperties";
import { useConfirm, useToast } from "../../modules/message";
import { useOrgaoAetService } from "../../modules/business/common/commonBusiness";
import EntitySelectorCliente from "../cliente/EntitySelectorCliente.vue";

interface PlanilhaTrechosComponentParams {
  isSearch: boolean;
  selected: Array<PlanilhaTrechosPayload>;
  isNew?: boolean;
  isMultiple?: boolean;
  idPlanilhaTrechos?: number;
  idPlanilhaTrechosCopy?: number;
  preFilter?: PlanilhaTrechosFilterPayload;
}

interface PlanilhaTrechosState {
  planilhaTrechos: PlanilhaTrechosPayload | null;
  showEdit: boolean;
  results: PaginationResult<PlanilhaTrechosPayload>;
  filter: PlanilhaTrechosFilterPayload;
  page: number;
  stateReady: boolean;
  params: PlanilhaTrechosComponentParams;
  loadings: Array<Ref<boolean>>;
  stateId: string;
}

export default defineComponent({
  setup(props, { emit }) {
    const router = useRouter();

    const state = reactive<PlanilhaTrechosState>({
      planilhaTrechos: null,
      showEdit: false,
      stateReady: false,
      filter: usePlanilhaTrechosService().safeFilterPlanilhaTrecho(),
      page: 0,
      results: defaultResults(),
      loadings: [],
      params: {
        isSearch: props.isSearch,
        isNew: props.isNew,
        idPlanilhaTrechos: props.idPlanilhaTrechos,
        idPlanilhaTrechosCopy: props.idPlanilhaTrechosCopy,
        selected: props.selected ?? [],
        isMultiple: props.isMultiple,
        preFilter: props.preFilter
      },
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2)
    });

    copyObject(clearObject(props.preFilter), state.filter);

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIAllowed(authority, permissionInfo);
    };

    const amIBlocked = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIBlocked(authority, permissionInfo);
    };

    const getNome = (e?: PlanilhaTrechosPayload) => {
      return usePlanilhaTrechosService().getNomePlanilhaTrechos(e);
    };

    const stateLoading = computed(() => {
      return state.loadings.length !== 0;
    });

    const keyRegister = computed(() => {
      return state.showEdit
        ? state.planilhaTrechos?.idPlanilhaTrechos
          ? state.planilhaTrechos?.idPlanilhaTrechos
          : "new-" +
            Math.random()
              .toString(36)
              .substring(2)
        : null;
    });

    const executeSearch = () => {
      const {
        data,
        loading,
        searchPlanilhaTrechos
      } = usePlanilhaTrechosService();
      state.loadings.push(loading);
      return searchPlanilhaTrechos(state.filter, state.page)
        .then(() => {
          state.results = data.value;
          state.page = state.results.page.number;
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const submitSearch = () => {
      state.page = 0;
      state.results = defaultResults();
      return executeSearch();
    };

    const getParamsRouteEdit = (idPlanilhaTrechos: number) => {
      return {
        name: "trechos-planilha-edit",
        params: { idPlanilhaTrechos: idPlanilhaTrechos + "" }
      };
    };

    const processViewRoute = (
      route: Partial<RouteLocationNormalizedLoaded>
    ) => {
      if (
        route.name == "trechos-planilha-new" ||
        route.name == "trechos-planilha-edit" ||
        route.name == "trechos-planilha-copy"
      ) {
        const {
          newPlanilhaTrechos,
          getPlanilhaTrechos,
          copyPlanilhaTrechos,
          data,
          loading
        } = usePlanilhaTrechosService();
        let p: Promise<void>;
        state.loadings.push(loading);
        if (route.name == "trechos-planilha-new") {
          p = newPlanilhaTrechos(props.cliente?.idCliente);
        } else if (route.name == "trechos-planilha-edit") {
          let id: number | undefined;
          if (route.params && route.params.idPlanilhaTrechos) {
            id = Array.isArray(route.params.idPlanilhaTrechos)
              ? +route.params.idPlanilhaTrechos[0]
              : +route.params.idPlanilhaTrechos;
          }
          if (!id) {
            throw "illegal id";
          }
          p = getPlanilhaTrechos(id);
        } else {
          let id: number | undefined;
          if (route.params && route.params.idPlanilhaTrechosCopy) {
            id = Array.isArray(route.params.idPlanilhaTrechosCopy)
              ? +route.params.idPlanilhaTrechosCopy[0]
              : +route.params.idPlanilhaTrechosCopy;
          }
          if (!id) {
            throw "illegal id";
          }
          p = copyPlanilhaTrechos(id);
        }
        p.then(() => {
          state.planilhaTrechos = data.value;
          state.showEdit = true;
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        }).catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
      } else {
        state.showEdit = false;
        state.planilhaTrechos = null;
        executeSearch();
      }
    };

    const setDefaultEntity = (planilhaTrechos: PlanilhaTrechosPayload) => {
      useConfirm().require({
        message:
          "Deseja alterar a Planilha " +
          getNome(planilhaTrechos) +
          " como Padrão?",
        header: "Atenção",
        icon: "pi pi-info-circle",
        acceptClass: "p-button-danger",
        accept: () => {
          if (planilhaTrechos && planilhaTrechos.idPlanilhaTrechos) {
            const {
              setDefaultPlanilhaTrechos,
              loading
            } = usePlanilhaTrechosService();
            state.loadings.push(loading);
            setDefaultPlanilhaTrechos(planilhaTrechos.idPlanilhaTrechos)
              .then(() => {
                executeSearch();
                state.loadings.splice(state.loadings.indexOf(loading), 1);
              })
              .catch(() => {
                state.loadings.splice(state.loadings.indexOf(loading), 1);
              });
          }
        }
      });
    };
    const idsPlanilhasMae = computed(() => {
      return new Set(
        useOrgaoAetService()
          .getOrgaosAet()
          .map((o) => o.idPlanilhaMae)
          .filter((id) => id != null)
          .map((id) => Number(id))
      );
    });

    const isPlanilhaMae = (idPlanilhaTrechos?: number | null) => {
      return (
        idPlanilhaTrechos != null &&
        idsPlanilhasMae.value.has(Number(idPlanilhaTrechos))
      );
    };

    const sincronizarPlanilhaMae = (
      planilhaTrechos: PlanilhaTrechosPayload
    ) => {
      const id = planilhaTrechos?.idPlanilhaTrechos;
      if (!id) {
        return;
      }
      useConfirm().require({
        message:
          "Propagar as atualizações desta planilha mãe para todas as planilhas derivadas dos clientes? Apenas trechos já selecionados serão atualizados ou removidos.",
        header: "Sincronizar planilhas derivadas",
        icon: "pi pi-info-circle",
        accept: () => {
          const {
            data,
            loading,
            sincronizarPlanilhaGovPass
          } = usePlanilhaTrechosService();
          state.loadings.push(loading);
          sincronizarPlanilhaGovPass(id)
            .then(() => {
              const r = data.value;
              useToast().success(
                "Sincronização concluída: " +
                  r.planilhasAfetadas +
                  " planilha(s), " +
                  r.trechosAtualizados +
                  " trecho(s) atualizado(s), " +
                  r.trechosRemovidos +
                  " removido(s)."
              );
              state.loadings.splice(state.loadings.indexOf(loading), 1);
            })
            .catch(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
            });
        }
      });
    };

    const shelveEntity = (planilhaTrechos: PlanilhaTrechosPayload) => {
      useConfirm().require({
        message: "Deseja arquivar a Planilha " + getNome(planilhaTrechos) + "?",
        header: "Atenção",
        icon: "pi pi-info-circle",
        acceptClass: "p-button-danger",
        accept: () => {
          if (planilhaTrechos && planilhaTrechos.idPlanilhaTrechos) {
            const {
              shelvePlanilhaTrechos,
              loading
            } = usePlanilhaTrechosService();
            state.loadings.push(loading);
            shelvePlanilhaTrechos(planilhaTrechos.idPlanilhaTrechos)
              .then(() => {
                executeSearch();
                state.loadings.splice(state.loadings.indexOf(loading), 1);
              })
              .catch(() => {
                state.loadings.splice(state.loadings.indexOf(loading), 1);
              });
          }
        }
      });
    };

    const unshelveEntity = (planilhaTrechos: PlanilhaTrechosPayload) => {
      useConfirm().require({
        message:
          "Deseja desarquivar a Planilha " + getNome(planilhaTrechos) + "?",
        header: "Atenção",
        icon: "pi pi-info-circle",
        acceptClass: "p-button-danger",
        accept: () => {
          if (planilhaTrechos && planilhaTrechos.idPlanilhaTrechos) {
            const {
              unshelvePlanilhaTrechos,
              loading
            } = usePlanilhaTrechosService();
            state.loadings.push(loading);
            unshelvePlanilhaTrechos(planilhaTrechos.idPlanilhaTrechos)
              .then(() => {
                executeSearch();
                state.loadings.splice(state.loadings.indexOf(loading), 1);
              })
              .catch(() => {
                state.loadings.splice(state.loadings.indexOf(loading), 1);
              });
          }
        }
      });
    };

    const confirmSelection = (selected: Array<PlanilhaTrechosPayload>) => {
      emit("confirm-selection", selected);
    };

    const cancelSelection = () => {
      emit("cancel-selection");
    };

    const openEntity = (
      idPlanilhaTrechos: number,
      force?: boolean,
      newTab?: boolean
    ) => {
      const routeParams = getParamsRouteEdit(idPlanilhaTrechos);
      if (newTab) {
        window.open(router.resolve(routeParams).fullPath);
      } else if (state.params.isSearch || force) {
        processViewRoute(routeParams);
      } else {
        router.push(routeParams);
      }
    };

    const newEntity = (force?: boolean) => {
      const routeParams = {
        name: "trechos-planilha-new"
      };
      if (state.params.isSearch || force) {
        processViewRoute(routeParams);
      } else {
        router.push(routeParams);
      }
    };

    const generateCopy = (idPlanilhaTrechosCopy: number, force?: boolean) => {
      const routeParams = {
        name: "trechos-planilha-copy",
        params: { idPlanilhaTrechosCopy: idPlanilhaTrechosCopy + "" }
      };
      if (state.params.isSearch || force) {
        processViewRoute(routeParams);
      } else {
        router.push(routeParams);
      }
    };

    const afterSetOrgao = (idOrgao?: number | null) => {
      state.filter.idOrgao = idOrgao;
    };

    const afterRegisterScreenClosed = (
      planilhaTrechos?: PlanilhaTrechosPayload
    ) => {
      const routeParams = {
        name: "trechos-planilha"
      };
      if (state.params.isSearch) {
        if (state.params.idPlanilhaTrechos) {
          if (planilhaTrechos && planilhaTrechos.idPlanilhaTrechos) {
            confirmSelection([planilhaTrechos]);
          } else {
            cancelSelection();
          }
        } else {
          if (
            planilhaTrechos &&
            indexesOfElement(
              state.params.selected,
              planilhaTrechos,
              (ob) => ob && ob.idPlanilhaTrechos
            ).length <= 0
          ) {
            useConfirm().require({
              message: "Deseja selecionar o registro salvo?",
              header: "Confirmação",
              icon: "pi pi-info-circle",
              accept: () => {
                toggleElement(
                  state.params.selected,
                  planilhaTrechos,
                  (ob) => ob && ob.idPlanilhaTrechos,
                  state.params.isMultiple
                );
                processViewRoute(routeParams);
              },
              reject: () => {
                processViewRoute(routeParams);
              }
            });
          } else {
            processViewRoute(routeParams);
          }
        }
      } else {
        router.push(routeParams);
      }
    };

    const afterSaveNewEntity = (idPlanilhaTrechos: number) => {
      openEntity(idPlanilhaTrechos);
    };

    const afterPageChange = (page: number) => {
      state.page = page;
      executeSearch();
    };

    const clearFilter = () => {
      state.filter = usePlanilhaTrechosService().safeFilterPlanilhaTrecho();
      state.results = defaultResults();
      copyObject(clearObject(props.preFilter), state.filter);
    };

    const getNomeCliente = (e?: ClientePayload) => {
      return (amIAllowed("view", "root-cliente")
        ? useClienteService()
        : useClienteLogadoService()
      ).getNomeCliente(e);
    };

    const confirmSelectionCliente = (c?: ClientePayload) => {
      state.filter.idCliente = c && c.idCliente ? c.idCliente : undefined;
    };

    const deveAbrirTelaCadastro =
      state.params.idPlanilhaTrechos || state.params.isNew;
    if (deveAbrirTelaCadastro) {
      if (state.params.idPlanilhaTrechos) {
        openEntity(state.params.idPlanilhaTrechos, true);
      } else if (state.params.idPlanilhaTrechosCopy) {
        generateCopy(state.params.idPlanilhaTrechosCopy, true);
      } else {
        newEntity(true);
      }
    }

    onMounted(async () => {
      if (props.cliente && props.cliente.idCliente) {
        state.filter.idCliente = props.cliente.idCliente;
      }
      state.stateReady = true;
      if (!state.params.isSearch) {
        watch([router.currentRoute], () => {
          const currRoute = router.currentRoute.value;
          processViewRoute(currRoute);
        });
      }
      if (!deveAbrirTelaCadastro) {
        submitSearch().then(() => {
          if (
            state.params.preFilter &&
            state.params.preFilter.idOrgao &&
            state.results.content.length == 1
          ) {
            const arr = [] as Array<PlanilhaTrechosPayload>;
            state.params.selected.forEach((el) => arr.push(el));
            addSetElement(
              arr,
              state.results.content[0],
              (ob) => ob && ob.idPlanilhaTrechos,
              state.params.isMultiple
            );
            confirmSelection(arr);
          } else {
            focusOnLastElement();
          }
        });
      }
    });

    return {
      submitSearch,
      executeSearch,
      openEntity,
      newEntity,
      generateCopy,
      setDefaultEntity,
      shelveEntity,
      unshelveEntity,
      isPlanilhaMae,
      sincronizarPlanilhaMae,
      confirmSelection,
      cancelSelection,
      afterRegisterScreenClosed,
      afterSaveNewEntity,
      afterPageChange,
      clearFilter,
      keyRegister,
      format,
      amIAllowed,
      amIBlocked,
      afterSetOrgao,
      getNome,
      getNomeCliente,
      getParamsRouteEdit,
      stateLoading,
      confirmSelectionCliente,
      ...toRefs(state)
    };
  },
  components: {
    CadastroPlanilhaTrechos,
    EntitySelectorOrgaoAet,
    EntitySelectorCliente
  },
  props: {
    isSearch: Boolean,
    isNew: Boolean,
    isMultiple: Boolean,
    idPlanilhaTrechos: Number,
    idPlanilhaTrechosCopy: Number,
    preFilter: Object as PropType<PlanilhaTrechosFilterPayload>,
    cliente: Object as PropType<ClientePayload>,
    selected: {
      type: Array as PropType<Array<PlanilhaTrechosPayload>>
    }
  }
});
